import { useEffect, useState } from "react";
import { Search, Zap, Trash2 } from "lucide-react";
import { toast } from "sonner";
import apiClient, { getErrorMessage } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { AdminHeader, TableWrap, Th, Td, Tr, StatusChip, Spinner, EmptyRow } from "@/components/admin/ui";

const ROLES = ["customer", "staff", "admin"];
const OWNER_EMAIL = "miker@pitlanesimracing.com";

export default function AdminUsers() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const canManageRoles = me?.email === OWNER_EMAIL;

  const load = () => {
    apiClient.get("/admin/users").then(({ data }) => setUsers(data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const changeRole = async (userId, role) => {
    try {
      await apiClient.put(`/admin/users/${userId}/role?role=${role}`);
      setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, role } : u)));
      toast.success(`Role updated to ${role}`);
    } catch (err) {
      toast.error(getErrorMessage(err, "Only the owner account can change roles"));
    }
  };

  const isAdmin = me?.role === "admin";
  const deleteUser = async (u) => {
    if (!window.confirm(`Delete ${u.first_name} ${u.last_name} (${u.email})? Their account is removed; bookings/chat history is kept.`)) return;
    try {
      await apiClient.delete(`/admin/users/${u.id}`);
      setUsers((prev) => prev.filter((x) => x.id !== u.id));
      toast.success("User deleted");
    } catch (err) {
      toast.error(getErrorMessage(err, "Could not delete user"));
    }
  };
  const toggleLeaderboard = async (userId, include) => {
    setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, leaderboard_include: include } : u)));
    try {
      await apiClient.put(`/admin/users/${userId}/leaderboard-include`, { include });
      toast.success(include ? "Now shown on leaderboards" : "Hidden from leaderboards");
    } catch (err) {
      setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, leaderboard_include: !include } : u)));
      toast.error(getErrorMessage(err, "Failed to update"));
    }
  };

  const filtered = users.filter((u) => {
    const s = q.toLowerCase();
    return !s || `${u.first_name} ${u.last_name} ${u.email}`.toLowerCase().includes(s);
  });

  if (loading) return <Spinner label="Loading users" />;

  return (
    <div>
      <AdminHeader
        title="Users"
        subtitle={`${users.length} registered users`}
        right={
          <div className="relative">
            <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <input className="input-cyber pl-10 w-64" placeholder="Search name or email" value={q}
              onChange={(e) => setQ(e.target.value)} data-testid="user-search" />
          </div>
        }
      />
      <TableWrap>
        <thead><Tr>
          <Th>Name</Th><Th>Email</Th><Th>Role</Th><Th>Points</Th><Th>Member</Th><Th>Leaderboard</Th><Th>Joined</Th>{isAdmin && <Th>Actions</Th>}
        </Tr></thead>
        <tbody>
          {filtered.length === 0 ? <EmptyRow colSpan={isAdmin ? 8 : 7} text="No users found" /> : filtered.map((u) => (
            <Tr key={u.id}>
              <Td className="text-white font-semibold" data-testid={`user-name-${u.id}`}>{u.first_name} {u.last_name}</Td>
              <Td>{u.email}</Td>
              <Td>
                {canManageRoles ? (
                  <select value={u.role} onChange={(e) => changeRole(u.id, e.target.value)}
                    data-testid={`user-role-${u.id}`}
                    className="bg-slate-900 border border-white/10 rounded-lg px-2 py-1 text-slate-200 capitalize">
                    {ROLES.map((r) => <option key={r} value={r} className="bg-slate-900">{r}</option>)}
                  </select>
                ) : (
                  <span data-testid={`user-role-${u.id}`}><StatusChip status={u.role} /></span>
                )}
              </Td>
              <Td><span className="inline-flex items-center gap-1" style={{ color: "#ffb020" }}><Zap className="w-3.5 h-3.5" />{(u.points ?? 0).toLocaleString()}</span></Td>
              <Td>{u.is_member ? <StatusChip status="active" /> : <span className="text-slate-500">—</span>}</Td>
              <Td>
                {(u.role === "admin" || u.role === "customer" || u.role === "staff") ? (
                  isAdmin ? (
                    <button
                      onClick={() => toggleLeaderboard(u.id, !u.leaderboard_include)}
                      data-testid={`user-leaderboard-toggle-${u.id}`}
                      title={u.leaderboard_include ? "Shown on leaderboards — click to hide" : "Hidden from leaderboards — click to show"}
                      className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors"
                      style={{ background: u.leaderboard_include ? "#00e5ff" : "rgba(120,150,220,0.25)" }}
                    >
                      <span className="inline-block h-5 w-5 transform rounded-full bg-white transition-transform"
                        style={{ transform: u.leaderboard_include ? "translateX(22px)" : "translateX(2px)" }} />
                    </button>
                  ) : (
                    u.leaderboard_include ? <StatusChip status="active" /> : <span className="text-slate-500">—</span>
                  )
                ) : <span className="text-slate-500">—</span>}
              </Td>
              <Td className="text-slate-400">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</Td>
              {isAdmin && (
                <Td>
                  {u.role !== "admin" && u.email !== OWNER_EMAIL && u.id !== me?.id ? (
                    <button onClick={() => deleteUser(u)} data-testid={`user-delete-${u.id}`}
                      className="text-slate-500 hover:text-pink-400 transition-colors" title="Delete user">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  ) : <span className="text-slate-600">—</span>}
                </Td>
              )}
            </Tr>
          ))}
        </tbody>
      </TableWrap>
    </div>
  );
}
